import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DiscountCouponService from "./Discountcoupons.service";
import BusinessService from "../Business/Businesses.service.js";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import PermissionAccess from "../../PermissionAccess/Access.js";
import { getFileUrl } from "../../utils/fileUtils";
import { SensitiveImage, SensitiveText, useSensitiveInfo } from "../../context/SensitiveInfoContext";
import ImagePreviewModal from "../../components/Modals/ImagePreviewModal";

const DoctorICReviewModal = ({ show, onClose, doctorId, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authData } = PermissionAccess();
  const { isProtected, isUnlocked, requestUnlock } = useSensitiveInfo();
  const [loading, setLoading] = useState(false);
  const [doctor, setDoctor] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [form, setForm] = useState({
    IC_Verification: "",
    reason: "",
    ic_no: "",
    ic_ocr_number: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      requestUnlock();
    } else {
      setPreviewImage(null);
    }
  }, [show, requestUnlock]);

  useEffect(() => {
    const load = async () => {
      if (!show || !doctorId) return;
      setLoading(true);
      try {
        const resp = await BusinessService.GetSingleDoctor(doctorId);
        setLoading(false);
        if (resp.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
          return;
        }
        if (resp.data.status) {
          const row = resp.data.data?.data || resp.data.data || {};
          setDoctor(row);
          setForm({
            IC_Verification: row.IC_Verification || "",
            reason: row.ic_verification_reason || "",
            ic_no: row.ic_no || "",
            ic_ocr_number: row.ic_ocr_number || "",
          });
        } else {
          Alerts.swalErrorAlert(t("Something went wrong, Please try again."));
        }
      } catch (error) {
        setLoading(false);
        Alerts.swalErrorAlert(error?.message || t("Server Error, Please try again later."));
      }
    };
    load();
  }, [show, doctorId, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRecheckOcr = async () => {
    setLoading(true);
    try {
      const response = await DiscountCouponService.checkDoctorICVerification(
        authData.sessionID,
        doctorId
      );
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
        return;
      }
      if (response.data.status) {
        Alerts.swalSuccessAlert(response.data.message || t("OCR check completed"));
        onSuccess?.();
        onClose?.();
      } else {
        Alerts.swalErrorAlert(response.data.message || t("OCR check failed"));
      }
    } catch (error) {
      setLoading(false);
      Alerts.swalErrorAlert(error?.message || t("OCR check failed"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = {};
    if (!form.IC_Verification) validation.IC_Verification = t("IC verification status is required");
    if (
      (form.IC_Verification === "rejected" || form.IC_Verification === "re-submission") &&
      !String(form.reason || "").trim()
    ) {
      validation.reason = t("Reason is required");
    }
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    setLoading(true);
    try {
      const response = await DiscountCouponService.adminUpdateDoctorICVerification(
        authData.sessionID,
        {
          doctor_id: doctorId,
          IC_Verification: form.IC_Verification,
          reason: form.reason,
          ic_no: form.ic_no,
          ic_ocr_number: form.ic_ocr_number,
        }
      );
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
        return;
      }
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("IC verification updated successfully."));
        onSuccess?.();
        onClose?.();
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Update failed"));
      }
    } catch (error) {
      setLoading(false);
      Alerts.swalErrorAlert(error?.message || t("Server Error, Please try again later."));
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal d-block mt-4"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      tabIndex="-1"
      role="dialog"
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <LoadingOverlay active={loading} spinner={<DNALoader />}>
              <div className="modal-header">
                <h5 className="modal-title">{t("IC Verification Review")}</h5>
                <button type="button" className="btn-close" onClick={onClose} />
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>{t("Doctor")}:</strong> {doctor?.full_name || "-"}
                  </div>
                  <div className="col-md-6">
                    <strong>{t("Current Status")}:</strong>{" "}
                    {doctor?.IC_Verification || "pending"}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">{t("Entered NRIC")}</label>
                    {!isProtected || isUnlocked ? (
                    <input
                      className="form-control"
                      name="ic_no"
                      value={form.ic_no}
                      onChange={handleChange}
                    />
                    ) : (
                      <div className="form-control bg-light">
                        <SensitiveText value={form.ic_no} />
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{t("OCR Extracted NRIC")}</label>
                    {!isProtected || isUnlocked ? (
                    <input
                      className="form-control"
                      name="ic_ocr_number"
                      value={form.ic_ocr_number}
                      onChange={handleChange}
                    />
                    ) : (
                      <div className="form-control bg-light">
                        <SensitiveText value={form.ic_ocr_number} />
                      </div>
                    )}
                  </div>
                </div>

                {doctor?.ic_image ? (
                  <div className="mb-3 text-center">
                    <label className="form-label d-block">{t("IC Image")}</label>
                    <SensitiveImage
                      src={getFileUrl(doctor.ic_image)}
                      alt="IC"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 280,
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                      placeholderStyle={{ width: 280, height: 160, borderRadius: 8 }}
                      onClick={() => setPreviewImage(getFileUrl(doctor.ic_image))}
                    />
                    <small className="text-muted d-block mt-1">
                      {t("Click image to view full size")}
                    </small>
                  </div>
                ) : (
                  <p className="text-muted">{t("No IC image uploaded")}</p>
                )}

                <div className="mb-3">
                  <label className="form-label">{t("IC Verification")}</label>
                  <select
                    className="form-control"
                    name="IC_Verification"
                    value={form.IC_Verification}
                    onChange={handleChange}
                  >
                    <option value="">{t("Select")}</option>
                    <option value="pending">{t("Pending")}</option>
                    <option value="approved">{t("Approved")}</option>
                    <option value="rejected">{t("Rejected")}</option>
                    <option value="re-submission">{t("Re-submission")}</option>
                  </select>
                  {errors.IC_Verification && (
                    <small className="text-danger">{errors.IC_Verification}</small>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">{t("Reason")}</label>
                  <textarea
                    className="form-control"
                    name="reason"
                    rows={3}
                    value={form.reason}
                    onChange={handleChange}
                    placeholder={t("Required for reject / re-submission")}
                  />
                  {errors.reason && <small className="text-danger">{errors.reason}</small>}
                </div>

                <p className="text-muted small mb-0">
                  {t("Note")}: {t("This updates IC_Verification only — not account Pending/Approved/Rejected status.")}
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={onClose}>
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleRecheckOcr}
                >
                  {t("Re-run OCR")}
                </button>
                <button type="submit" className="btn btn-custom-color">
                  {t("Save")}
                </button>
              </div>
            </LoadingOverlay>
          </div>
        </div>
      </form>
      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        src={previewImage}
        title={t("IC Image")}
      />
    </div>
  );
};

export default DoctorICReviewModal;
